/**
 * FormSubmitter.js
 * -------------------------------
 * Extracted form submission logic from ContactModal.js
 * Provides reusable form submission methods with retry logic, endpoint fallbacks, and CSRF protection.
 *
 * Features:
 * - Multiple endpoint support with automatic fallbacks
 * - CSRF token handling and automatic refresh
 * - Configurable retry logic with exponential backoff
 * - Request/response transformation hooks
 * - Comprehensive error handling and categorization
 * - Progress tracking and timeout management
 * - Flexible data preparation and validation
 * - Analytics integration support
 * - Request deduplication to prevent double submissions
 */

import { orgContactPhrase } from '../../utils/orgContact.js';

export class FormSubmitter {
  constructor(options = {}) {
    this.options = {
      // Default endpoints to try
      endpoints: ['/api/contact', '/contact'],
      
      // Request configuration
      timeout: 30000, // 30 seconds
      retryAttempts: 2,
      retryDelay: 1000, // Initial delay between retries
      retryBackoff: 2, // Exponential backoff multiplier
      
      // Request headers
      defaultHeaders: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      
      // CSRF configuration
      csrf: {
        enabled: true,
        tokenSelector: 'input[name="_csrf"]', // CSS selector for CSRF token input
        headerName: 'x-csrf-token', // Header name for CSRF token
        cookieName: '__Host-psifi.x-csrf-token', // Cookie name (for csrf-csrf)
        apiEndpoint: '/api/csrf-token', // Endpoint to get fresh token
        metaSelector: 'meta[name="csrf-token"]', // Meta tag selector
        autoRefresh: true // Automatically refresh token on 403 errors
      },
      
      // Analytics tracking
      enableAnalytics: true,
      analyticsHandler: null,
      
      // Request deduplication
      preventDuplicates: true,
      duplicateWindow: 2000, // 2 seconds
      
      // Debug logging
      debug: false,
      
      ...options
    };
    
    this.activeRequests = new Map();
    this.recentSubmissions = new Map();
    this.csrfToken = null; // Cache CSRF token
  }

  /**
   * Get CSRF token from various sources
   * @returns {Promise<string|null>} CSRF token
   */
  async getCSRFToken() {
    if (!this.options.csrf.enabled) {
      return null;
    }

    // Try to get token from cached value first
    if (this.csrfToken) {
      return this.csrfToken;
    }

    // Try to get token from hidden input
    const tokenInput = document.querySelector(this.options.csrf.tokenSelector);
    if (tokenInput && tokenInput.value) {
      this.csrfToken = tokenInput.value;
      return this.csrfToken;
    }

    // Try to get token from meta tag
    const metaToken = document.querySelector(this.options.csrf.metaSelector);
    if (metaToken && metaToken.content) {
      this.csrfToken = metaToken.content;
      return this.csrfToken;
    }

    // Try to get token from cookie (for csrf-csrf package)
    const cookieToken = this.getTokenFromCookie();
    if (cookieToken) {
      this.csrfToken = cookieToken;
      return this.csrfToken;
    }

    // No cached/DOM/cookie source available — fetch fresh from the server
    return this.fetchFreshCSRFToken();
  }

  /**
   * Fetch a brand-new CSRF token directly from the server, bypassing the
   * cache/DOM/cookie fallback chain in getCSRFToken(). Use this specifically
   * when recovering from a CSRF validation failure — the hidden input and
   * meta tag were rendered once at page load and are exactly what just went
   * stale, so re-reading them would hand back the same invalid token.
   * @returns {Promise<string|null>} Fresh CSRF token, or null on failure
   */
  async fetchFreshCSRFToken() {
    try {
      const response = await fetch(this.options.csrf.apiEndpoint, {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        this.csrfToken = data.csrfToken;

        // Keep the DOM in sync too, in case other code reads these directly
        const tokenInput = document.querySelector(this.options.csrf.tokenSelector);
        if (tokenInput) {
          tokenInput.value = this.csrfToken;
        }
        const metaToken = document.querySelector(this.options.csrf.metaSelector);
        if (metaToken) {
          metaToken.content = this.csrfToken;
        }

        this.log('🔄 Fresh CSRF token obtained from API');
        return this.csrfToken;
      }
    } catch (err) {
      console.warn('Failed to fetch CSRF token from API:', err);
    }

    return null;
  }

  /**
   * Extract CSRF token from cookie (for csrf-csrf package)
   * @returns {string|null} Token from cookie
   */
  getTokenFromCookie() {
    if (typeof document === 'undefined') return null;
    
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === this.options.csrf.cookieName) {
        try {
          return decodeURIComponent(value);
        } catch (err) {
          console.warn('Failed to decode CSRF cookie:', err);
          return null;
        }
      }
    }
    return null;
  }

  /**
   * Clear cached CSRF token (forces refresh on next request)
   */
  clearCSRFToken() {
    this.csrfToken = null;
    this.log('🗑️ CSRF token cache cleared');
  }

  /**
   * Prepare form data for submission
   * @param {HTMLFormElement|FormData|Object} form - The form or data to prepare
   * @param {Object} options - Preparation options
   * @returns {Object} Prepared data object
   */
  static prepareFormData(form, options = {}) {
    const defaults = {
      includeMetadata: true,
      excludeFields: [],
      transformFields: {},
      additionalData: {}
    };
    
    const config = { ...defaults, ...options };
    let formData;
    
    // Convert form to FormData if needed
    if (form instanceof HTMLFormElement) {
      formData = new FormData(form);
    } else if (form instanceof FormData) {
      formData = form;
    } else if (typeof form === 'object') {
      // Convert object to FormData
      formData = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach(v => formData.append(key, v));
        } else {
          formData.append(key, value);
        }
      });
    } else {
      throw new Error('Invalid form data type');
    }
    
    // Extract data from FormData
    const data = {};
    
    for (const [key, value] of formData.entries()) {
      if (config.excludeFields.includes(key)) continue;
      
      // Handle multiple values (checkboxes, etc.)
      if (data[key]) {
        if (!Array.isArray(data[key])) {
          data[key] = [data[key]];
        }
        data[key].push(value);
      } else {
        data[key] = value;
      }
    }
    
    // Apply field transformations
    Object.entries(config.transformFields).forEach(([field, transformer]) => {
      if (data[field] && typeof transformer === 'function') {
        data[field] = transformer(data[field]);
      }
    });
    
    // Add metadata if enabled
    if (config.includeMetadata && typeof window !== 'undefined') {
      data._metadata = {
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        referrer: document.referrer,
        screenResolution: `${screen.width}x${screen.height}`,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: navigator.language
      };
    }
    
    // Add additional data
    Object.assign(data, config.additionalData);
    
    return data;
  }

  /**
   * Submit form to multiple endpoints with retry logic and CSRF protection (called from ContactModal.js)
   * @param {HTMLFormElement|FormData|Object} form - The form or data to submit
   * @param {Object} options - Submission options
   * @returns {Promise<Object>} Submission result
   */
  async submit(form, options = {}) {
    const config = { ...this.options, ...options };
    const submissionId = this.generateSubmissionId(form);
    
    try {
      // Check for duplicate submissions
      if (config.preventDuplicates && this.isDuplicateSubmission(submissionId)) {
        throw new Error('Duplicate submission detected');
      }
      
      // Track active request
      if (this.activeRequests.has(submissionId)) {
        throw new Error('Submission already in progress');
      }
      
      // Get CSRF token if enabled
      let csrfToken = null;
      if (config.csrf.enabled) {
        csrfToken = await this.getCSRFToken();
        if (!csrfToken) {
          throw new Error('CSRF token not available');
          console.log('❌ CSRF token not available');
        }
      }
      
      // Prepare data with CSRF token
      const data = FormSubmitter.prepareFormData(form, {
        ...config,
        additionalData: {
          ...config.additionalData,
          ...(csrfToken && { _csrf: csrfToken }) // Include CSRF token in form data
        }
      });
      
      this.log('📤 Starting form submission with CSRF protection', { 
        submissionId, 
        hasCsrfToken: !!csrfToken,
        data: { ...data, _csrf: csrfToken ? '[REDACTED]' : undefined }
      });
      
      // Track submission start
      this.trackSubmission(submissionId, 'start');
      this.activeRequests.set(submissionId, Date.now());

      // Try endpoints with retry logic
      const result = await this.tryEndpoints(data, config, submissionId, csrfToken);
      
      // Track success
      this.trackSubmission(submissionId, 'success', { result });
      this.recordSuccessfulSubmission(submissionId);
      
      this.log('✅ Form submission successful', { submissionId, result });
      
      return {
        success: true,
        data: result,
        submissionId,
        timestamp: new Date().toISOString()
      };
      
    } catch (error) {
      this.log('❌ Form submission failed', { submissionId, error: error.message });
      
      // Track failure
      this.trackSubmission(submissionId, 'error', { error: error.message });
      
      throw this.categorizeError(error);
      
    } finally {
      this.activeRequests.delete(submissionId);
    }
  }

  /**
   * Try multiple endpoints with fallback logic
   * @param {Object} data - Data to submit
   * @param {Object} config - Configuration options
   * @param {string} submissionId - Unique submission identifier
   * @param {string} csrfToken - CSRF token
   * @returns {Promise<Object>} Response data
   */
  async tryEndpoints(data, config, submissionId, csrfToken) {
    const endpoints = Array.isArray(config.endpoints) ? config.endpoints : [config.endpoints];
    let lastError;
    let currentToken = csrfToken;
    // A freshly-minted token bound to the current session should always
    // validate on the very next request — if it's rejected too, refreshing
    // again won't help (the problem is elsewhere, e.g. cookies blocked
    // entirely), so cap this to a single attempt rather than looping
    // indefinitely on i-- below.
    let csrfRefreshAttempted = false;

    console.log('📢 Trying to submit to endpoints:', endpoints, csrfToken);
    
    for (let i = 0; i < endpoints.length; i++) {
      const endpoint = endpoints[i];
      const isLastEndpoint = i === endpoints.length - 1;
      
      try {
        this.log(`🔄 Trying endpoint ${i + 1}/${endpoints.length}: ${endpoint}`);
        
        const result = await this.submitToEndpoint(endpoint, data, config, submissionId, currentToken);
        
        this.log(`✅ Endpoint ${endpoint} succeeded`);
        console.log(`✅ Endpoint ${endpoint} succeeded`);
        return result;
        
      } catch (error) {
        lastError = error;
        this.log(`❌ Endpoint ${endpoint} failed: ${error.message}`);
        console.log(`❌ Endpoint ${endpoint} failed: ${error.message}`);
        // Records whether a refresh had already been attempted (at either
        // retry layer) by the time this error was caught, so categorizeError()
        // can tell "stale token, never refreshed" apart from "refreshed and
        // it still failed" (the latter usually means cookies are blocked
        // entirely, not just expired — see csrfRefreshAttempted below).
        error.csrfRefreshAttempted = error.csrfRefreshAttempted || csrfRefreshAttempted;

        // Handle CSRF token errors specially (single refresh attempt only — see csrfRefreshAttempted above)
        if (this.isCSRFError(error) && config.csrf.enabled && config.csrf.autoRefresh && !csrfRefreshAttempted) {
          this.log('🔄 CSRF error detected, attempting token refresh');
          console.log('📢 CSRF error detected, attempting token refresh');
          csrfRefreshAttempted = true;
          this.clearCSRFToken(); // Clear cached token

          try {
            const freshToken = await this.fetchFreshCSRFToken();
            if (freshToken && freshToken !== currentToken) {
              currentToken = freshToken;
              // Update data with new token
              data._csrf = freshToken;
              this.log('✅ Fresh CSRF token obtained, retrying same endpoint');
              console.log('✅ Fresh CSRF token obtained, retrying same endpoint');

              // Retry the same endpoint with fresh token (don't increment i)
              i--;
              continue;
            }
          } catch (tokenError) {
            this.log('❌ Failed to refresh CSRF token:', tokenError);
            console.log('❌ Failed to refresh CSRF token:', tokenError);
          }
        }
        
        // Don't try next endpoint for certain errors
        if (this.shouldStopTrying(error, csrfRefreshAttempted)) {
          throw error;
        }
        
        // If this is the last endpoint, throw the error
        if (isLastEndpoint) {
          throw error;
        }
        
        // Wait before trying next endpoint
        if (config.retryDelay > 0) {
          await this.delay(config.retryDelay);
        }
      }
    }
    
    throw lastError || new Error('All endpoints failed');
  }

  /**
   * Submit to a specific endpoint with retry logic and CSRF protection
   * @param {string} endpoint - API endpoint URL
   * @param {Object} data - Data to submit
   * @param {Object} config - Configuration options
   * @param {string} submissionId - Unique submission identifier
   * @param {string} csrfToken - CSRF token
   * @returns {Promise<Object>} Response data
   */
  async submitToEndpoint(endpoint, data, config, submissionId, csrfToken) {
    let attempt = 0;
    let lastError;
    let currentToken = csrfToken;
    // See tryEndpoints() for why this is capped to a single attempt.
    let csrfRefreshAttempted = false;

    while (attempt <= config.retryAttempts) {
      try {
        const isRetry = attempt > 0;
        if (isRetry) {
          this.log(`🔄 Retry attempt ${attempt}/${config.retryAttempts} for ${endpoint}`);
        }
        
        // Create request options with CSRF token
        const headers = { 
          ...config.defaultHeaders, 
          ...config.headers
        };
        
        // Add CSRF token to headers if available
        if (currentToken && config.csrf.enabled) {
          headers[config.csrf.headerName] = currentToken;
        }
        
        const requestOptions = {
          method: 'POST',
          headers,
          body: JSON.stringify(data),
          credentials: 'same-origin' // Include cookies for CSRF
        };
        
        // Add timeout if supported
        if (config.timeout > 0) {
          const controller = new AbortController();
          requestOptions.signal = controller.signal;
          
          setTimeout(() => controller.abort(), config.timeout);
        }
        
        // Apply request transformation hook
        if (typeof config.transformRequest === 'function') {
          config.transformRequest(requestOptions, { endpoint, attempt, submissionId });
        }
        
        // Make the request
        const response = await fetch(endpoint, requestOptions);
        
        // Check response status
        if (!response.ok) {
          const errorText = await response.text();
          const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
          error.status = response.status;
          error.statusText = response.statusText;
          error.responseText = errorText;
          throw error;
        }
        
        // Parse response
        let result;
        const contentType = response.headers.get('content-type');
        
        if (contentType && contentType.includes('application/json')) {
          result = await response.json();
        } else {
          result = { message: await response.text() };
        }
        
        // Apply response transformation hook
        if (typeof config.transformResponse === 'function') {
          result = config.transformResponse(result, { endpoint, attempt, submissionId });
        }
        
        return result;
        
      } catch (error) {
        lastError = error;
        attempt++;
        // See the identical comment in tryEndpoints() — same purpose.
        error.csrfRefreshAttempted = error.csrfRefreshAttempted || csrfRefreshAttempted;

        // Handle CSRF token errors specially (single refresh attempt only — see csrfRefreshAttempted above)
        if (this.isCSRFError(error) && config.csrf.enabled && config.csrf.autoRefresh && !csrfRefreshAttempted) {
          this.log('🔄 CSRF error on retry, attempting token refresh');
          csrfRefreshAttempted = true;
          this.clearCSRFToken();

          try {
            const freshToken = await this.fetchFreshCSRFToken();
            if (freshToken && freshToken !== currentToken) {
              currentToken = freshToken;
              data._csrf = freshToken; // Update data
              this.log('✅ Fresh CSRF token obtained for retry');
              // Don't increment attempt for CSRF token refresh
              attempt--;
              // Skip the stop-checks below and retry immediately — falling
              // through would re-check shouldStopRetrying with
              // csrfRefreshAttempted already true (just set above) and stop
              // us before we ever use the token we just fetched.
              continue;
            }
          } catch (tokenError) {
            this.log('❌ Failed to refresh CSRF token on retry:', tokenError);
          }
        }

        // Don't retry certain errors
        if (this.shouldStopRetrying(error, csrfRefreshAttempted)) {
          throw error;
        }
        
        // If we've exhausted retries, throw the error
        if (attempt > config.retryAttempts) {
          throw error;
        }
        
        // Calculate delay with exponential backoff
        const delay = config.retryDelay * Math.pow(config.retryBackoff, attempt - 1);
        this.log(`⏳ Waiting ${delay}ms before retry...`);
        await this.delay(delay);
      }
    }
    
    throw lastError;
  }

  /**
   * Check if error is CSRF-related
   * @param {Error} error - Error to check
   * @returns {boolean} True if CSRF error
   */
  isCSRFError(error) {
    if (error.status === 403) {
      const errorText = error.responseText || error.message || '';
      return errorText.toLowerCase().includes('csrf') || 
             errorText.toLowerCase().includes('token') ||
             errorText.includes('INVALID_CSRF_TOKEN');
    }
    return false;
  }

  /**
   * Generate unique submission ID
   * @param {*} form - Form data
   * @returns {string} Unique submission ID
   */
  generateSubmissionId(form) {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substr(2, 9);
    
    // Include a hash of form data for deduplication
    let dataHash = '';
    if (form instanceof HTMLFormElement) {
      dataHash = form.action || form.id || 'form';
    } else if (typeof form === 'object') {
      dataHash = JSON.stringify(form).substr(0, 50);
    }
    
    return `${timestamp}-${random}-${btoa(dataHash).substr(0, 8)}`;
  }

  /**
   * Check if this is a duplicate submission
   * @param {string} submissionId - Submission ID to check
   * @returns {boolean} True if duplicate
   */
  isDuplicateSubmission(submissionId) {
    const now = Date.now();
    const window = this.options.duplicateWindow;
    
    // Clean old submissions
    for (const [id, timestamp] of this.recentSubmissions.entries()) {
      if (now - timestamp > window) {
        this.recentSubmissions.delete(id);
      }
    }
    
    // Check for recent similar submission
    const baseId = submissionId.split('-').slice(0, -1).join('-');
    
    for (const [id] of this.recentSubmissions.entries()) {
      const otherBaseId = id.split('-').slice(0, -1).join('-');
      if (baseId === otherBaseId) {
        return true;
      }
    }
    
    return false;
  }

  /**
   * Record successful submission for deduplication
   * @param {string} submissionId - Submission ID
   */
  recordSuccessfulSubmission(submissionId) {
    this.recentSubmissions.set(submissionId, Date.now());
  }

  /**
   * Determine if we should stop trying other endpoints
   * @param {Error} error - The error that occurred
   * @param {boolean} [csrfRefreshAttempted=false] - Whether a token refresh has
   *   already been tried for this error. A CSRF error gets one exemption from
   *   the generic "stop on 4xx" rule so the refresh-and-retry path can run;
   *   once that's been tried and it still failed, treat it like any other
   *   client error and stop — the same stale token would just be resent.
   * @returns {boolean} True if we should stop trying
   */
  shouldStopTrying(error, csrfRefreshAttempted = false) {
    // Stop for validation errors (400-level), but not CSRF errors on their first pass
    if (error.status >= 400 && error.status < 500 && !(this.isCSRFError(error) && !csrfRefreshAttempted)) {
      return true;
    }

    // Stop for certain error types
    if (error.name === 'ValidationError' || error.name === 'AuthenticationError') {
      return true;
    }

    return false;
  }

  /**
   * Determine if we should stop retrying the current endpoint
   * @param {Error} error - The error that occurred
   * @param {boolean} [csrfRefreshAttempted=false] - See shouldStopTrying — same
   *   one-shot CSRF exemption applies here.
   * @returns {boolean} True if we should stop retrying
   */
  shouldStopRetrying(error, csrfRefreshAttempted = false) {
    // Rate limiting - log and stop retrying immediately
    if (error.status === 429) {
      console.warn('⏱️ Rate limit exceeded (429 Too Many Requests) - stopping retry attempts');
      return true;
    }

    // Don't retry validation errors; a CSRF error gets one exemption until a refresh has been tried
    if (error.status >= 400 && error.status < 500 && !(this.isCSRFError(error) && !csrfRefreshAttempted)) {
      return true;
    }

    // Don't retry certain error types
    if (error.name === 'AbortError' || error.name === 'ValidationError') {
      return true;
    }

    return false;
  }

  /**
   * Categorize error for better handling
   * @param {Error} error - The error to categorize
   * @returns {Error} Categorized error
   */
  categorizeError(error) {
    const categorizedError = new Error(error.message);
    categorizedError.originalError = error;

    if (error.message.includes('Duplicate submission')) {
      categorizedError.category = 'duplicate';
      categorizedError.userMessage = 'This form was already submitted recently.';
    } else if (error.message.includes('already in progress')) {
      categorizedError.category = 'in_progress';
      categorizedError.userMessage = 'Please wait for the current submission to complete.';
    } else if (error.message.includes('CSRF token not available')) {
      categorizedError.category = 'csrf';
      categorizedError.userMessage = 'Security token missing. Please refresh the page and try again.';
    } else if (this.isCSRFError(error)) {
      categorizedError.category = 'csrf';
      categorizedError.csrfRefreshAttempted = !!error.csrfRefreshAttempted;
      if (error.csrfRefreshAttempted) {
        // A fresh, correctly-bound token was already fetched and resent, and
        // it still failed — refreshing again won't change that. This usually
        // means the browser isn't keeping cookies at all (Private Browsing,
        // a strict privacy extension), which no amount of retrying fixes.
        categorizedError.userMessage = 'We couldn’t verify your submission, even after retrying. This usually means your browser is blocking cookies for this site — common in Private/Incognito mode or with some privacy extensions. Try a regular browser window, or reach us directly at ' + orgContactPhrase() + '.';
      } else {
        categorizedError.userMessage = 'Security token invalid. Please refresh the page and try again.';
      }
    } else if (error.status === 429) {
      // Rate limiting
      categorizedError.category = 'rate_limited';
      categorizedError.userMessage = 'Too many submissions. Please wait a moment before trying again.';

      // Log detailed rate limit info
      if (error.responseText) {
        try {
          const responseData = JSON.parse(error.responseText);
          if (responseData.rateLimitStatus) {
            console.error('🚫 Rate Limit Details:', {
              isLimited: responseData.rateLimitStatus.isLimited,
              isBlocked: responseData.rateLimitStatus.isBlocked,
              blockExpiresAt: responseData.rateLimitStatus.blockExpiresAt,
              message: responseData.message
            });
          }
        } catch (e) {
          // If response is not JSON, just log the text
          console.error('🚫 Rate Limited - Response:', error.responseText);
        }
      }
    } else if (error.status >= 400 && error.status < 500) {
      categorizedError.category = 'validation';
      categorizedError.userMessage = 'Please check your information and try again.';
    } else if (error.status >= 500 || error.name === 'NetworkError') {
      categorizedError.category = 'server';
      categorizedError.userMessage = 'Server error. Please try again later.';
    } else if (error.name === 'AbortError') {
      categorizedError.category = 'timeout';
      categorizedError.userMessage = 'Request timed out. Please try again.';
    } else {
      categorizedError.category = 'unknown';
      categorizedError.userMessage = 'An unexpected error occurred. Please try again.';
    }

    return categorizedError;
  }

  /**
   * Track submission events (for analytics)
   * @param {string} submissionId - Submission ID
   * @param {string} event - Event type (start, success, error)
   * @param {Object} data - Additional event data
   */
  trackSubmission(submissionId, event, data = {}) {
    if (!this.options.enableAnalytics) return;
    
    const eventData = {
      submissionId,
      event: `form_submission_${event}`,
      timestamp: new Date().toISOString(),
      csrf_enabled: this.options.csrf.enabled,
      ...data
    };
    
    if (typeof this.options.analyticsHandler === 'function') {
      this.options.analyticsHandler(eventData);
    } else if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', eventData.event, eventData);
    }

    console.log('📊 Analytics event tracked:', eventData);
  }

  /**
   * Log debug information
   * @param {string} message - Log message
   * @param {Object} data - Additional log data
   */
  log(message, data = {}) {
    if (this.options.debug) {
      console.log('📢',`[FormSubmitter] ${message}`, data);
    }
  }

  /**
   * Delay utility function
   * @param {number} ms - Milliseconds to delay
   * @returns {Promise} Promise that resolves after delay
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Create a FormSubmitter instance with contact form defaults and CSRF
   * @param {Object} options - Override options
   * @returns {FormSubmitter} Configured FormSubmitter instance
   */
  static createContactSubmitter(options = {}) {
    return new FormSubmitter({
      endpoints: ['/api/contact', '/contact'],
      retryAttempts: 2,
      retryDelay: 1000,
      timeout: 30000,
      debug: false,
      
      // Contact form specific transformations
      transformFields: {
        firstName: (value) => value.trim(),
        lastName: (value) => value.trim(),
        email: (value) => value.trim().toLowerCase(),
        phone: (value) => value.replace(/\D/g, ''), // Remove non-digits
        message: (value) => value.trim()
      },
      
      additionalData: {
        source: 'contact_modal',
        type: 'contact_form'
      },
      
      // CSRF configuration for contact forms
      csrf: {
        enabled: true,
        tokenSelector: 'input[name="_csrf"]',
        headerName: 'x-csrf-token',
        cookieName: '__Host-psifi.x-csrf-token',
        apiEndpoint: '/api/csrf-token',
        metaSelector: 'meta[name="csrf-token"]',
        autoRefresh: true
      },
      
      ...options
    });
  }

  /**
   * Create a FormSubmitter instance with newsletter defaults and CSRF
   * @param {Object} options - Override options
   * @returns {FormSubmitter} Configured FormSubmitter instance
   */
  static createNewsletterSubmitter(options = {}) {
    return new FormSubmitter({
      endpoints: ['/api/newsletter', '/newsletter'],
      retryAttempts: 3,
      retryDelay: 500,
      timeout: 15000,
      
      transformFields: {
        email: (value) => value.trim().toLowerCase()
      },
      
      additionalData: {
        source: 'newsletter_signup',
        type: 'newsletter_form'
      },
      
      // CSRF configuration
      csrf: {
        enabled: true,
        tokenSelector: 'input[name="_csrf"]',
        headerName: 'x-csrf-token',
        cookieName: '__Host-psifi.x-csrf-token',
        apiEndpoint: '/api/csrf-token',
        metaSelector: 'meta[name="csrf-token"]',
        autoRefresh: true
      },
      
      ...options
    });
  }

  /**
   * Create a FormSubmitter instance without CSRF protection
   * @param {Object} options - Override options
   * @returns {FormSubmitter} Configured FormSubmitter instance
   */
  static createUnsecuredSubmitter(options = {}) {
    return new FormSubmitter({
      csrf: {
        enabled: false
      },
      ...options
    });
  }

  /**
   * Test CSRF token functionality
   * @returns {Promise<Object>} Test results
   */
  async testCSRFToken() {
    if (!this.options.csrf.enabled) {
      return {
        success: false,
        message: 'CSRF protection is disabled',
        csrfEnabled: false
      };
    }

    try {
      const token = await this.getCSRFToken();
      
      return {
        success: !!token,
        message: token ? 'CSRF token obtained successfully' : 'Failed to obtain CSRF token',
        csrfEnabled: true,
        tokenLength: token ? token.length : 0,
        tokenSource: this.determineTokenSource()
      };
    } catch (error) {
      return {
        success: false,
        message: `CSRF token test failed: ${error.message}`,
        csrfEnabled: true,
        error: error.message
      };
    }
  }

  /**
   * Determine where the CSRF token came from
   * @returns {string} Token source
   */
  determineTokenSource() {
    if (this.csrfToken) {
      const tokenInput = document.querySelector(this.options.csrf.tokenSelector);
      if (tokenInput && tokenInput.value === this.csrfToken) {
        return 'hidden_input';
      }
      
      const metaToken = document.querySelector(this.options.csrf.metaSelector);
      if (metaToken && metaToken.content === this.csrfToken) {
        return 'meta_tag';
      }
      
      const cookieToken = this.getTokenFromCookie();
      if (cookieToken === this.csrfToken) {
        return 'cookie';
      }
      
      return 'api';
    }
    
    return 'none';
  }
}